/**
 * Selector único de priorización comercial.
 *
 * Responde una sola pregunta: de toda la cartera con trabajo pendiente,
 * ¿a quién debe atender primero el Director Comercial? Cualquier pantalla
 * que necesite esa respuesta (Home, y en el futuro Turn/Wallet) debe usar
 * este módulo — nunca reimplementar el criterio localmente.
 *
 * Niveles (de mayor a menor prioridad; un nivel gana siempre al siguiente,
 * nunca se mezclan por antigüedad):
 *   1. Cliente interesado esperando seguimiento — ya respondió y sigue abierto.
 *   2. Renovación más próxima — motor "renewal", ordenada por cercanía real.
 *   3. Cliente recuperable con mayor oportunidad — motor "recovery", por valor.
 *   4. Venta cruzada — motor "cross_sell".
 *   5. Resto — cualquier otra oportunidad pendiente.
 *
 * Dentro de un mismo nivel, el desempate usa la señal más antigua sin
 * atender (updatedAt ascendente) — la antigüedad NUNCA decide el nivel,
 * solo rompe empates dentro de él.
 *
 * Enriquecimientos futuros (no implementados todavía, a propósito): valor
 * esperado ponderado por probabilidad, SLA explícito, tiempo sin atender
 * como score continuo, prioridad manual del Socio Fundador. Cuando se
 * agreguen, entran aquí — nunca duplicados en un componente de UI.
 */

function levelOf(opp) {
  if (opp.outcome !== "pending") return null;
  if (["replied", "interested"].includes(opp.responseStatus)) return 1;
  if (opp.engineId === "renewal") return 2;
  if (opp.engineId === "recovery") return 3;
  if (opp.engineId === "cross_sell") return 4;
  return 5;
}

function reasonFor(opp, level) {
  switch (level) {
    case 1:
      return opp.responseStatus === "interested"
        ? "Ya mostró interés — sigue esperando el siguiente paso."
        : "Ya respondió — sigue esperando que retomes la conversación.";
    case 2:
      return opp.windowDays != null
        ? `Su renovación vence en ${opp.windowDays} días.`
        : "Su renovación está próxima a vencer.";
    case 3:
      return "Es el cliente con mayor oportunidad de recuperar en tu cartera.";
    case 4:
      return "Ya está listo para comprarte algo más.";
    default:
      return "Es la siguiente conversación pendiente en tu cartera.";
  }
}

const byOldestUpdate = (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt);

/**
 * Explica, para UNA sola oportunidad, en qué nivel de prioridad cae y por
 * qué — el mismo criterio de `prioritizeOpportunities`, aplicado a un solo
 * registro. Úsalo en cualquier pantalla que muestre una oportunidad y deba
 * responder "¿por qué me trajiste aquí?" (p. ej. OpportunityDetail) sin
 * reimplementar los niveles.
 *
 * @param {Object} opp
 * @returns {{ level: number|null, reason: string|null }}
 */
export function explainPriority(opp) {
  const level = levelOf(opp);
  return { level, reason: level != null ? reasonFor(opp, level) : null };
}

/**
 * Ordena oportunidades por prioridad comercial real y adjunta, a cada una,
 * el nivel asignado (`priorityLevel`) y el motivo humano de esa elección
 * (`priorityReason`). Oportunidades ya resueltas (won/lost) quedan fuera.
 *
 * @param {Array} opportunities
 * @returns {Array} misma forma que `opportunities`, ordenada, con
 *   `priorityLevel`/`priorityReason` añadidos.
 */
export function prioritizeOpportunities(opportunities) {
  return (opportunities || [])
    .map((opp) => ({ opp, level: levelOf(opp) }))
    .filter((x) => x.level != null)
    .sort((x, y) => {
      if (x.level !== y.level) return x.level - y.level;
      // Dentro de "renovación más próxima": la más próxima primero.
      if (x.level === 2) {
        const diff = (x.opp.windowDays ?? Infinity) - (y.opp.windowDays ?? Infinity);
        if (diff !== 0) return diff;
      }
      // Dentro de "recuperable con mayor oportunidad": el mayor valor primero.
      if (x.level === 3) {
        const diff =
          (y.opp.estimatedValue?.value.amount ?? 0) - (x.opp.estimatedValue?.value.amount ?? 0);
        if (diff !== 0) return diff;
      }
      return byOldestUpdate(x.opp, y.opp);
    })
    .map(({ opp, level }) => ({
      ...opp,
      priorityLevel: level,
      priorityReason: reasonFor(opp, level),
    }));
}

/**
 * Conveniencia para Home: la conversación siguiente y el resto ya ordenado.
 * @param {Array} opportunities
 * @returns {{ next: Object|null, rest: Array }}
 */
export function pickNextConversation(opportunities) {
  const ranked = prioritizeOpportunities(opportunities);
  return { next: ranked[0] || null, rest: ranked.slice(1) };
}
